// ============================================================================
// CALENDAR — the shapes and the pure derivations behind docs/design/calendar-brief.md.
//
// Here rather than in either app because the Worker and the browser have to agree about three
// things or the screen and the API tell different stories about the same week:
//   · WHAT A BLOCK IS. One vocabulary for the five lanes of the source palette, one flag for
//     "LifeOS owns this", one flag for "this is only a suggestion".
//   · WHERE A BLOCK SITS. `top = startHour * 46 + (startMin / 60) * 46` is written once, tested
//     once, and used by every view. A second copy is how a week grid and a day grid start
//     disagreeing by two pixels at 4 PM.
//   · WHAT A DEADLINE SAYS. The due-row copy is LITERAL from Canvas (brief rule 5), including
//     the case where Canvas gives no time — which says "no time given" and never 11:59 PM.
//
// THREE HONESTY RULES LIVE IN THIS FILE, not in a component:
//   1. `editable` is a field, not a CSS class. Canvas blocks come back with `editable: false`
//      and the view renders no affordance at all — not a greyed-out one (brief rule 1).
//   2. `state: 'suggested'` is excluded from every count here. `placedMinutes`, `summariseDay`
//      and `countedBlocks` all filter it out, so "3 events" can never quietly include a
//      proposal nobody accepted (brief rule 2).
//   3. `summariseDay` states its own denominator and names the remainder. It never estimates
//      unscheduled time; it subtracts (brief rule 3).
// ============================================================================

import { minutesToClockCompact } from './dates.js'
import type { ISODate, ISOTimestamp, Task } from './types.js'

// ── The grid, as constants rather than magic numbers ────────────────────────

/**
 * THE WINDOW THE GRID DRAWS: the whole day, midnight to midnight.
 *
 * It was 7 AM – 10 PM, and that was a lie about the owner's calendar rather than a layout choice:
 * a 6 AM run, a 10:30 PM lecture recording and a midnight deadline all existed and none of them
 * could be drawn, moved, or even clicked into being. The grid is 24 hours tall and SCROLLS
 * (see `CALENDAR_DAY_START_HOUR` for where it opens), which is the only version of this window
 * that can never hide an hour the owner actually used.
 */
export const CALENDAR_START_HOUR = 0
export const CALENDAR_END_HOUR = 24
/** Pixels per hour. The brief's number, and the mockup's: hour rules land at 46px intervals. */
export const CALENDAR_HOUR_PX = 46
/** Everything a drag produces snaps to this (brief → Day view extras). */
export const CALENDAR_SNAP_MINUTES = 15

/** Minutes of wall clock the DRAWN grid covers — 1440. The denominator of every pixel, not of
 *  any count: see `CALENDAR_DAY_MINUTES` for the number the day summary divides by. */
export const CALENDAR_WINDOW_MINUTES = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * 60

/**
 * THE PLANNING DAY — 7 AM – 10 PM, and it is NOT what is drawn.
 *
 * Two jobs, both of which would be wrong against a 24-hour denominator:
 *   · the grid OPENS scrolled to this window, so the first thing on screen is still the stretch
 *     of day the owner actually schedules in, exactly as it was before the grid grew;
 *   · "Where today goes" divides by it, and the calendar proposes slots inside it. A bar that
 *     reported three placed hours as a share of 24 would call a full day mostly empty, and a
 *     suggestion engine that saw midnight as free would propose work at 3 AM.
 *
 * Blocks OUTSIDE it are drawn, dragged and edited like any other — this window bounds what the
 * app SAYS about the day, never what it will show of it.
 */
export const CALENDAR_DAY_START_HOUR = 7
export const CALENDAR_DAY_END_HOUR = 22
/** 900 — the stated denominator of the day summary (brief rule 3). */
export const CALENDAR_DAY_MINUTES = (CALENDAR_DAY_END_HOUR - CALENDAR_DAY_START_HOUR) * 60

/** The hour labels down the gutter, 1 AM … 11 PM — the boundaries, not the rule at midnight. */
export function calendarHourMarks(): number[] {
  const out: number[] = []
  for (let h = CALENDAR_START_HOUR + 1; h < CALENDAR_END_HOUR; h++) out.push(h)
  return out
}

// ── The vocabulary ──────────────────────────────────────────────────────────

/**
 * The lane a block belongs to. This is the SOURCE PALETTE, not the connector vocabulary that
 * `tasks.source` and `assignments.source` share — see the calendar migration's header.
 *
 * TWO OF THE SIX ARE READ-ONLY AND BOTH ARE READ-ONLY THE SAME WAY. 'canvas' is produced by
 * deriving a class or a deadline out of what the Canvas connector wrote; 'google' is produced by
 * reading the `gcal_events` mirror the iCal connector wrote. Neither is in
 * `CALENDAR_WRITABLE_SOURCES`, so neither can be created or patched through the calendar API at
 * all — the read-only rule is a type error, not a UI convention.
 */
export type CalendarBlockSource = 'personal' | 'work' | 'learning' | 'task' | 'canvas' | 'google'

/** The four lanes LifeOS can actually store. `canvas` and `google` are absent, structurally. */
export const CALENDAR_WRITABLE_SOURCES = ['personal', 'work', 'learning', 'task'] as const
export type CalendarWritableSource = (typeof CALENDAR_WRITABLE_SOURCES)[number]

/**
 * Accepted, or merely proposed. A proposal is visible and labelled and carries Keep / Dismiss;
 * it is not counted, not summarised and not reminded on until it is accepted (brief rule 2).
 */
export type CalendarBlockState = 'accepted' | 'suggested'

export interface CalendarBlock {
  id: string
  title: string
  source: CalendarBlockSource
  state: CalendarBlockState
  /** The local calendar day the block starts on — what column it belongs in. */
  date: ISODate
  startsAt: ISOTimestamp
  endsAt: ISOTimestamp
  /** Minutes past local midnight. The grid's only geometry input; derived, never stored. */
  startMinutes: number
  endMinutes: number
  location: string | null
  /**
   * GOOGLE'S OWN STATUS WORD, for a TIMED Google event only — null for every other source,
   * because nothing else in this domain has a concept of "tentative". `tentative` is drawn as
   * tentative and never quietly promoted to `confirmed` — the same rule
   * `CalendarAllDayEvent.status` already keeps for whole-day events, extended to the grid.
   */
  status: 'confirmed' | 'tentative' | 'cancelled' | null
  /** The task this block holds time for, when it holds time for one. */
  taskId: string | null
  /**
   * FALSE MEANS NO AFFORDANCE EXISTS — no drag handle, no resize edge, no delete, no edit
   * fields. Not a disabled control (brief rule 1). Only LifeOS-owned blocks are true.
   */
  editable: boolean
  /** Set when this block was derived from a course. Null on anything the owner made. */
  courseCode: string | null
  courseSlug: string | null
  /**
   * Set when this row was created by accepting a chat proposal — the id of that proposal.
   *
   * PROVENANCE IS A LINK, NOT A SOURCE VALUE. `source` above is the owner's LANE of the week and
   * a connector name in it is a bug (docs/design/calendar-brief.md, and the calendar migration
   * says so in as many words). So "the chat suggested this and I accepted it" is a foreign key,
   * and the grid reads THIS field to draw FROM CHAT the way it draws FROM CANVAS.
   */
  proposalId: string | null
  /**
   * WHEN TO BE TOLD ABOUT THIS BLOCK — minutes BEFORE `startsAt`, Google-Calendar style. `0` is
   * "at start"; several are allowed, because "the day before AND ten minutes before" is one
   * intention and not two blocks. `[]` — the normal case — means no reminder was asked for.
   *
   * Only a block LifeOS owns can carry these. A Canvas class and a Google event come back with
   * none, for the same reason they come back `editable: false`: there is no row here to hang a
   * reminder on, and offering one would be an affordance over nothing (brief rule 1).
   *
   * OPTIONAL IN THIS TYPE, ALWAYS PRESENT ON THE WIRE. The API states it on every block
   * (`CalendarBlockSchema` requires it), and the field is optional here only so the demo
   * fixtures and views written before notifications existed keep meaning what they meant:
   * absent reads as "no reminders", which is exactly what they said. Read it as
   * `block.reminders ?? []`.
   */
  reminders?: number[]
}

/**
 * A deadline in the all-day row. NOT a block: it has an instant, not a duration, and putting it
 * on the grid would mean inventing a length for it.
 */
export interface CalendarDueItem {
  id: string
  assignmentId: string
  /** The local day the deadline falls on. */
  date: ISODate
  /** Canvas's own title, unchanged. */
  title: string
  dueAt: ISOTimestamp | null
  /**
   * FALSE means Canvas gave a day and no time. The label then says "no time given" and nothing
   * anywhere assumes 11:59 PM (brief rule 5).
   */
  hasTime: boolean
  /**
   * Minutes past local midnight, or null when `hasTime` is false. DERIVED alongside `date` so
   * that a countdown never needs the browser to re-derive a local time from an instant — the
   * two would disagree on exactly two nights a year.
   */
  dueMinutes: number | null
  /** The literal due-row copy: `PS4 · 11:59p`, or `Quiz 3 opens · no time given`. */
  label: string
  courseCode: string | null
  courseSlug: string | null
  status: 'upcoming' | 'submitted' | 'graded' | 'missing'
}

/**
 * A Google Calendar event that covers whole days rather than a span of clock time.
 *
 * IT IS NOT A BLOCK, and the reason is the same one that keeps deadlines off the grid: an
 * all-day event has no time of day, so drawing it on a 7 AM–10 PM grid would mean the screen
 * choosing an hour Google never gave. It goes in the all-day row instead, exactly where the
 * grid already keeps the things that have a day and no time.
 */
export interface CalendarAllDayEvent {
  id: string
  /** The ICS UID (plus an instance stamp when the row is one occurrence of a series). */
  externalId: string
  source: 'google'
  title: string
  /** The first local day it covers. */
  date: ISODate
  /** The last local day it covers, INCLUSIVE. A one-day event has `endDate === date`. */
  endDate: ISODate
  startsAt: ISOTimestamp
  endsAt: ISOTimestamp
  location: string | null
  /** Google's own STATUS. `tentative` is drawn as tentative, never quietly as confirmed. */
  status: 'confirmed' | 'tentative' | 'cancelled'
  /** Always false. A mirror of somebody else's calendar has nothing to edit here. */
  editable: false
}

/** The span of days the Google mirror actually covers. Absence outside it proves nothing. */
export interface CalendarMirrorWindow {
  from: ISODate
  to: ISODate
}

/** One ranged read of the calendar: what LifeOS owns, and what Canvas and Google are owed. */
export interface CalendarRange {
  from: ISODate
  to: ISODate
  blocks: CalendarBlock[]
  dueItems: CalendarDueItem[]
  /** Google's whole-day events for the range. Timed Google events ride in `blocks`. */
  allDayEvents: CalendarAllDayEvent[]
  /**
   * HIDDEN IS NOT MISSING. The connector mirrors a rolling window, not all of history, so
   * "no Google events" outside that window means "not synced", not "nothing scheduled". Null
   * when Google has never synced at all — and then the screen says that instead.
   */
  googleWindow: CalendarMirrorWindow | null
}

// ── Geometry ────────────────────────────────────────────────────────────────

export interface BlockBox {
  /** Pixels from the top of the grid body. */
  top: number
  height: number
  /**
   * True when the block is shorter than ~30px, i.e. under 40 minutes. The brief: "Under ~30px
   * height, drop the time line and keep one line of text only."
   */
  compact: boolean
}

/**
 * The brief's formula, written once. Clamped to the WINDOW, and to nothing else.
 *
 * THERE IS DELIBERATELY NO MINIMUM HEIGHT. A 15-minute block is 11.5px tall at 46px an hour, and
 * it stays 11.5px: a floor would make short blocks overlap the ones after them, and a grid where
 * two blocks appear to collide when they do not is worse than a strip that is hard to read. The
 * ~30px rule (`compact`) is the brief's own answer to the legibility problem — drop the time
 * line, keep one line of text.
 */
export function blockBox(startMinutes: number, endMinutes: number): BlockBox {
  const windowStart = CALENDAR_START_HOUR * 60
  const windowEnd = CALENDAR_END_HOUR * 60
  const start = Math.max(startMinutes, windowStart)
  const end = Math.min(Math.max(endMinutes, start + CALENDAR_SNAP_MINUTES), windowEnd)
  const top = ((start - windowStart) / 60) * CALENDAR_HOUR_PX
  const height = ((end - start) / 60) * CALENDAR_HOUR_PX
  return { top, height, compact: height < 30 }
}

/** Pixels from the top of the grid body for a bare wall-clock time — the now-line's rule. */
export function minutesToOffset(minutes: number): number {
  return ((minutes - CALENDAR_START_HOUR * 60) / 60) * CALENDAR_HOUR_PX
}

/** The inverse: what time a click at `offset` pixels means, snapped to 15 minutes. */
export function offsetToMinutes(offset: number): number {
  const raw = CALENDAR_START_HOUR * 60 + (offset / CALENDAR_HOUR_PX) * 60
  return snapToQuarter(raw)
}

/** Rounds to the nearest quarter hour and keeps the result inside the drawn window. */
export function snapToQuarter(minutes: number): number {
  const snapped = roundToQuarter(minutes)
  return Math.min(Math.max(snapped, CALENDAR_START_HOUR * 60), CALENDAR_END_HOUR * 60)
}

/** The snap with no clamp. Separate because a MOVE clamps the whole block, not just its start. */
function roundToQuarter(minutes: number): number {
  return Math.round(minutes / CALENDAR_SNAP_MINUTES) * CALENDAR_SNAP_MINUTES
}

/** The grid body's height in DESIGN pixels — 24 hours at 46px. What the CSS asks for, and what
 *  the scroll container is taller than: the body scrolls, opening at `CALENDAR_DAY_START_HOUR`. */
export const CALENDAR_GRID_PX = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * CALENDAR_HOUR_PX

/**
 * What time a pixel offset means, UNSNAPPED and UNCLAMPED — the exact inverse of
 * `minutesToOffset`.
 *
 * `offsetToMinutes` is the click's inverse and does both jobs at once, which is right for a
 * click and wrong for a drag: a drag has to snap the block's own edge and then clamp the WHOLE
 * block, and clamping the raw pointer first would shorten a block dragged past the window edge
 * instead of parking it there.
 *
 * `gridHeightPx` IS NOT AN OPTIMISATION, IT IS THE FIX FOR A REAL OFF-BY-AN-HOUR. The app runs at
 * `zoom: 1.3` by default (apps/web/src/lib/scale.ts — the owner's own Ctrl+= habit, made
 * permanent). Under CSS zoom a pointer's `clientY` and an element's `getBoundingClientRect()` are
 * both in ZOOMED pixels while `CALENDAR_HOUR_PX` is a design pixel, so dividing one by the other
 * reports 1.3 hours for every hour: at 130% a click on 10 AM composes an 11 AM block. Measuring
 * the column the pointer is actually over — its height IS the whole drawn day, in whatever units
 * the event is in — is right at every zoom, on every display, with no knowledge of the factor.
 * The default keeps the pure design-pixel reading for callers that have nothing to measure.
 *
 * THE SCROLL COSTS THIS NOTHING, and that is worth stating because the grid now scrolls. `offset`
 * is always measured from a rect the browser just returned (`clientY - box.top`), and a rect
 * moves with its scroll container — so a column scrolled halfway down reports a negative `top`
 * and the subtraction still lands on the hour under the pointer. No scroll offset is added
 * anywhere; adding one would double-count it.
 */
export function offsetToRawMinutes(offset: number, gridHeightPx: number = CALENDAR_GRID_PX): number {
  const height = gridHeightPx > 0 ? gridHeightPx : CALENDAR_GRID_PX
  return CALENDAR_START_HOUR * 60 + (offset / height) * CALENDAR_WINDOW_MINUTES
}

// ── Moving and resizing (the drag's arithmetic) ─────────────────────────────

/** How far the pointer must travel before a press stops counting as a click (grid.tsx uses it). */
export const CALENDAR_DRAG_THRESHOLD_PX = 4

/** A block's two edges, minutes past local midnight. What a move or a resize rewrites. */
export interface BlockSpan {
  startMinutes: number
  endMinutes: number
}

/**
 * Where a MOVED block lands: same length, snapped to 15 minutes, clamped into the drawn window.
 *
 * WHAT HAPPENS AT THE WINDOW EDGE — CLAMP, NEVER HIDE. Dragging above midnight parks the block at
 * 00:00; dragging past midnight parks it so its END sits on 24:00. The length never changes: a
 * drag is a move, and silently shortening a block because it met an edge would be the app
 * editing a duration the owner never touched.
 *
 * A BLOCK LONGER THAN THE DRAWN DAY has no legal start at all — `windowEnd - duration` is before
 * midnight. Its start clamps to 00:00 and the tail keeps running off the bottom, drawn clipped by
 * `blockBox` exactly as it is today. The alternative, refusing the move, would make one class of
 * block undraggable with no visible reason why.
 */
export function movedSpan(span: BlockSpan, proposedStartMinutes: number): BlockSpan {
  const windowStart = CALENDAR_START_HOUR * 60
  const windowEnd = CALENDAR_END_HOUR * 60
  const duration = Math.max(CALENDAR_SNAP_MINUTES, span.endMinutes - span.startMinutes)
  const latestStart = Math.max(windowStart, windowEnd - duration)
  const start = Math.min(Math.max(roundToQuarter(proposedStartMinutes), windowStart), latestStart)
  return { startMinutes: start, endMinutes: start + duration }
}

/**
 * Where a RESIZED block's bottom edge lands: snapped to 15 minutes, never shorter than 15, never
 * past the end of the drawn day.
 *
 * The start is untouched — this is the bottom handle, and a handle that moved both edges would
 * be a move wearing a resize's cursor. The 15-minute floor is the same number the snap uses, so
 * a block cannot be dragged into having no length and then be invisible on the grid.
 */
export function resizedSpan(span: BlockSpan, proposedEndMinutes: number): BlockSpan {
  const windowEnd = CALENDAR_END_HOUR * 60
  const shortest = span.startMinutes + CALENDAR_SNAP_MINUTES
  const end = Math.min(
    Math.max(roundToQuarter(proposedEndMinutes), shortest),
    Math.max(shortest, windowEnd),
  )
  return { startMinutes: span.startMinutes, endMinutes: end }
}

/** True when a drag ended exactly where it started — nothing to write, and nothing to undo. */
export function isSameSpan(a: BlockSpan, b: BlockSpan): boolean {
  return a.startMinutes === b.startMinutes && a.endMinutes === b.endMinutes
}

/** True when the wall-clock minute is inside the drawn window — the now-line's own test. Since
 *  the grid grew to the full day this is true of every real minute; it stays because the window
 *  is a constant somebody may narrow again, and a now-line drawn off the grid is worse than none. */
export function isInsideWindow(minutes: number): boolean {
  return minutes >= CALENDAR_START_HOUR * 60 && minutes <= CALENDAR_END_HOUR * 60
}

// ── Time copy ───────────────────────────────────────────────────────────────

/** '10:12' — the now pill, and the gutter's own 24h-free short form. */
export function clockHM(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h}:${String(Math.floor(minutes) % 60).padStart(2, '0')}`
}

/** '8 AM' / '12 PM' — the hour labels down the gutter. */
export function hourLabel(hour: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12
  return `${h} ${hour < 12 || hour === 24 ? 'AM' : 'PM'}`
}

/**
 * '11:59p' — the all-day row's compact form (brief rule 5's own example). One implementation,
 * in dates.ts, so the Month cell and the phone's class column cannot drift apart.
 */
export function compactTime(minutes: number): string {
  return minutesToClockCompact(minutes)
}

/**
 * '8:00 – 9:15 AM' · '11:30 AM – 1:00 PM'.
 *
 * ONE RULE, STATED: the meridiem is printed once, off the END time, and the start carries its
 * own only when the two straddle noon. The mockup prints it inconsistently across its blocks
 * (some 9:30 – 10:45, some 8:00 – 9:30 AM); a screen cannot be inconsistent, so this is the
 * rule the port adopts and the pair of screenshots is judged against.
 */
export function formatTimeRange(startMinutes: number, endMinutes: number): string {
  const startPm = Math.floor(startMinutes / 60) % 24 >= 12
  const endPm = Math.floor(endMinutes / 60) % 24 >= 12
  const meridiem = (pm: boolean) => (pm ? 'PM' : 'AM')
  return startPm === endPm
    ? `${clockHM(startMinutes)} – ${clockHM(endMinutes)} ${meridiem(endPm)}`
    : `${clockHM(startMinutes)} ${meridiem(startPm)} – ${clockHM(endMinutes)} ${meridiem(endPm)}`
}

/** '1h 30m' · '45m' · '2h'. Used for estimates, held time and the unscheduled remainder. */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  const h = Math.floor(m / 60)
  const rest = m % 60
  if (h === 0) return `${rest}m`
  if (rest === 0) return `${h}h`
  return `${h}h ${String(rest).padStart(2, '0')}m`
}

/**
 * The all-day row's literal copy. Title, then the time Canvas actually gave — or the words for
 * the fact that it gave none. Nothing here supplies a default hour (brief rule 5).
 */
export function dueLabel(title: string, hasTime: boolean, minutes: number | null): string {
  return hasTime && minutes !== null
    ? `${title} · ${compactTime(minutes)}`
    : `${title} · no time given`
}

// ── Tone: which palette a due item wears ────────────────────────────────────

/**
 * 'due' paints the red tint; 'canvas' paints the blue one.
 *
 * The brief: "Overdue/today-due uses the red tint; everything else the source colour." Work
 * that is already submitted or graded is NOT red however late the clock is — the deadline
 * stopped being a deadline the moment it was handed in, and colouring it red would be the
 * screen inventing an obligation that no longer exists.
 */
export function dueTone(item: CalendarDueItem, today: ISODate): 'due' | 'canvas' {
  const open = item.status === 'upcoming' || item.status === 'missing'
  return open && item.date <= today ? 'due' : 'canvas'
}

// ── Counting, and the honest denominator ────────────────────────────────────

/** Every block that is actually ON the calendar. Suggestions are not (brief rule 2). */
export function countedBlocks(blocks: readonly CalendarBlock[]): CalendarBlock[] {
  return blocks.filter((b) => b.state === 'accepted')
}

/**
 * The lanes the day summary groups into, in the order the mockup lists them.
 *
 * `google` IS ITS OWN LANE AND NOT 'personal'. The mockup drew four lanes because there were
 * four kinds of block; a mirrored Google event is a fifth kind, and folding it into Personal
 * would report time the owner never filed as Personal as if he had. The bar states what it
 * counted; a lane nobody has minutes in is dropped, so the four-lane day still draws four lanes.
 */
export type CalendarLane = 'class' | 'focus' | 'work' | 'personal' | 'google'

const LANE_OF: Record<CalendarBlockSource, CalendarLane> = {
  canvas: 'class',
  learning: 'focus',
  task: 'focus',
  work: 'work',
  personal: 'personal',
  google: 'google',
}

export const CALENDAR_LANE_LABELS: Record<CalendarLane, string> = {
  class: 'Class & discussion',
  focus: 'Focused work',
  work: 'Work',
  personal: 'Personal',
  google: 'From Google',
}

const LANE_ORDER: CalendarLane[] = ['class', 'focus', 'work', 'personal', 'google']

export interface DaySummaryLane {
  lane: CalendarLane
  label: string
  minutes: number
  /** Share of the STATED denominator, 0–1. Never of "the day". */
  share: number
}

export interface DaySummary {
  /** Always CALENDAR_DAY_MINUTES. Named in the copy so the bar can be checked against it. */
  windowMinutes: number
  /** Minutes of the window covered by accepted blocks. */
  placedMinutes: number
  /** windowMinutes − placedMinutes. Subtracted, never estimated (brief rule 3). */
  unscheduledMinutes: number
  lanes: DaySummaryLane[]
}

/**
 * "Where today goes", derived.
 *
 * TWO THINGS IT REFUSES TO DO. It does not count suggestions, and it does not guess what the
 * unscheduled hours hold — the remainder is arithmetic on a denominator the copy states out
 * loud, which is the entire content of brief rule 3.
 *
 * Minutes are clipped to the drawn window: a shift that runs to 11 PM contributes the part of
 * itself that is on the grid, because the bar is a share of what is shown.
 */
export function summariseDay(blocks: readonly CalendarBlock[]): DaySummary {
  // THE PLANNING DAY, NOT THE DRAWN GRID. The grid grew to 24 hours; this denominator did not,
  // because "3h of 24h" would report a full working day as an empty one. The copy beside the bar
  // names the same 7 AM – 10 PM out loud, which is the whole of brief rule 3.
  const windowStart = CALENDAR_DAY_START_HOUR * 60
  const windowEnd = CALENDAR_DAY_END_HOUR * 60
  const minutes = new Map<CalendarLane, number>()

  for (const block of countedBlocks(blocks)) {
    const start = Math.max(block.startMinutes, windowStart)
    const end = Math.min(block.endMinutes, windowEnd)
    if (end <= start) continue
    const lane = LANE_OF[block.source]
    minutes.set(lane, (minutes.get(lane) ?? 0) + (end - start))
  }

  const lanes = LANE_ORDER.filter((lane) => (minutes.get(lane) ?? 0) > 0).map((lane) => ({
    lane,
    label: CALENDAR_LANE_LABELS[lane],
    minutes: minutes.get(lane)!,
    share: minutes.get(lane)! / CALENDAR_DAY_MINUTES,
  }))

  const placedMinutes = lanes.reduce((sum, l) => sum + l.minutes, 0)
  return {
    windowMinutes: CALENDAR_DAY_MINUTES,
    placedMinutes,
    unscheduledMinutes: Math.max(0, CALENDAR_DAY_MINUTES - placedMinutes),
    lanes,
  }
}

// ── Suggestions ─────────────────────────────────────────────────────────────

/**
 * The tasks the calendar is allowed to propose a block for.
 *
 * THE GATE IS THE ESTIMATE, and it is the only gate that keeps brief rule 2 true: a suggestion
 * needs a length, the only honest length is one the owner gave, and a task without
 * `estimateMin` therefore produces no suggestion at all rather than a plausible hour.
 * Already-scheduled tasks drop out too — proposing time for work that already has time is the
 * calendar arguing with itself.
 */
export function suggestableTasks(
  tasks: readonly Task[],
  blocks: readonly CalendarBlock[],
): Task[] {
  const held = new Set(blocks.map((b) => b.taskId).filter((id): id is string => id !== null))
  return tasks.filter((t) => !t.done && t.estimateMin !== null && !held.has(t.id))
}

export interface SuggestedSlot {
  task: Task
  startMinutes: number
  endMinutes: number
}

/**
 * Where a suggested block would go, if the owner keeps it.
 *
 * IT FINDS A GAP; IT NEVER MAKES ONE. The search walks the day's accepted blocks in order and
 * takes the first opening — inside the drawn window, after `notBefore` — that the task's own
 * estimate fits in whole. If no such opening exists the task gets NO suggestion: shrinking the
 * estimate to fit would be the app rewriting a number the owner supplied, and overlapping an
 * existing block would be the app double-booking him.
 *
 * `notBefore` is the current wall clock on today, and the window's start on any other day. The
 * calendar does not propose to fill an hour that has already gone.
 *
 * IT SEARCHES THE PLANNING DAY, NOT THE DRAWN GRID. The grid now draws all 24 hours so that a
 * block the owner PUT at 6 AM can be seen; that is not the same as the app volunteering 3 AM as
 * a good time to do a problem set, which is what searching midnight-to-midnight would produce.
 */
export function suggestSlots(
  tasks: readonly Task[],
  blocks: readonly CalendarBlock[],
  notBefore: number,
  limit = 2,
): SuggestedSlot[] {
  const windowEnd = CALENDAR_DAY_END_HOUR * 60
  const taken = countedBlocks(blocks)
    .map((b) => ({ start: b.startMinutes, end: b.endMinutes }))
    .sort((a, b) => a.start - b.start)

  const out: SuggestedSlot[] = []
  for (const task of suggestableTasks(tasks, blocks)) {
    if (out.length >= limit) break
    const need = task.estimateMin!
    let cursor = snapToQuarter(Math.max(notBefore, CALENDAR_DAY_START_HOUR * 60))

    for (const busy of [...taken, ...out.map((s) => ({ start: s.startMinutes, end: s.endMinutes }))]
      .sort((a, b) => a.start - b.start)) {
      if (busy.end <= cursor) continue
      if (busy.start - cursor >= need) break
      cursor = snapToQuarter(busy.end)
    }

    if (cursor + need <= windowEnd) {
      out.push({ task, startMinutes: cursor, endMinutes: cursor + need })
    }
  }
  return out
}
